import React, { useEffect, useMemo } from "react";
import { useAppData } from "@/hooks/useAppData";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { Card, CardContent } from "@/lib/shadcn/ui/card";
import { DatePicker } from "@/lib/shadcn/components/date-picker";
import { getDate, getPreviousYearsDate, useOccupancyActionsDispatch, useOccupancySelector } from "@/store/occupancySlice";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import YearOverYearChart from "@/pages/graphs/year-over-year-chart";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { useGetOccupanciesQuery } from "@/store/booking-management-api";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useFilteredApartmentIds } from "@/hooks/useFilteredApartmentIds";

// Traceability: FR/EC IDs reference
// .verified/features/apartment-filters-graphs-and-database/spec.md.
// Occupancy Explorer: the four filters narrow both dropdowns; aggregation is
// delegated server-side via the `apartmentIds` query param on
// `/api/admin/occupancies`.
export const OccupanciesPage: React.FC = () => {
    const { setSelectedGroup, setSelectedApartment } = useUserSettingsDispatch();
    const { groupOptions, selectedGroup, selectedApartment, groupIdMap, apartmentDetailsMap } = useAppData();
    const apartmentOptions = useActiveApartmentOptions();
    const apartmentEntities = useActiveApartmentEntities();
    const filteredApartmentIds = useFilteredApartmentIds();

    const currentDate = useOccupancySelector(getDate);
    const previousYearsDate = useOccupancySelector(getPreviousYearsDate);
    const { setDate } = useOccupancyActionsDispatch();

    // Apartment options narrowed to the active filter (FR-004);
    // `filteredApartmentIds === null` means no filter → full list passes through.
    const filteredApartmentOptions = useMemo(() => {
        if (filteredApartmentIds === null) {
            return apartmentOptions;
        }
        const allowed = new Set(filteredApartmentIds);
        return apartmentOptions.filter((opt) => opt.value === "0" || allowed.has(Number(opt.value)));
    }, [apartmentOptions, filteredApartmentIds]);

    // Group options narrowed to groups with ≥ 1 filter-matching apartment
    // (FR-005); no filter → full list.
    const filteredGroupOptions = useMemo(() => {
        if (filteredApartmentIds === null) {
            return groupOptions;
        }
        const groupsWithMatchingApartments = new Set<number>();
        filteredApartmentIds.forEach((id) => {
            const apartment = apartmentDetailsMap.get(id);
            if (apartment) {
                groupsWithMatchingApartments.add(apartment.groupId);
            }
        });
        return groupOptions.filter((opt) => opt.value === "0" || groupsWithMatchingApartments.has(Number(opt.value)));
    }, [groupOptions, filteredApartmentIds, apartmentDetailsMap]);

    // Auto-reset `selectedApartment` to 0 ("All") when the filter excludes it
    // (EC-002); chart then re-fetches over the aggregate apartmentIds set.
    useEffect(() => {
        if (filteredApartmentIds === null) {
            return;
        }
        if (selectedApartment !== 0 && !filteredApartmentIds.includes(selectedApartment)) {
            setSelectedApartment(0);
        }
    }, [filteredApartmentIds, selectedApartment, setSelectedApartment]);

    // Auto-reset `selectedGroup` to 0 ("All") when the filter excludes every
    // apartment in the selected group (EC-002).
    useEffect(() => {
        if (filteredApartmentIds === null) {
            return;
        }
        if (selectedGroup === 0) {
            return;
        }
        const someApartmentInGroupMatches = filteredApartmentIds.some((id) => {
            const apartment = apartmentDetailsMap.get(id);
            return apartment !== undefined && apartment.groupId === selectedGroup;
        });
        if (!someApartmentInGroupMatches) {
            setSelectedGroup(0);
        }
    }, [filteredApartmentIds, selectedGroup, apartmentDetailsMap, setSelectedGroup]);

    // `apartmentIds` for the Group card, three-way (FR-005a/b):
    //   filter inactive          → undefined (backend branches on groupId as before)
    //   filter active, group=All → full filter-matching set
    //   filter active, group set → group ∩ filter
    const groupCardApartmentIds = useMemo<number[] | undefined>(() => {
        if (filteredApartmentIds === null) {
            return undefined;
        }
        if (selectedGroup === 0) {
            return filteredApartmentIds;
        }
        return filteredApartmentIds.filter((id) => apartmentDetailsMap.get(id)?.groupId === selectedGroup);
    }, [filteredApartmentIds, selectedGroup, apartmentDetailsMap]);

    // `apartmentIds` for the Apartment card (FR-005c):
    //   filter inactive              → undefined (backend as before)
    //   filter active, apartment set → [selectedApartment] (single-apt passthrough)
    //   filter active, apartment=All → full filter-matching set (portfolio aggregate)
    const apartmentCardApartmentIds = useMemo<number[] | undefined>(() => {
        if (filteredApartmentIds === null) {
            return undefined;
        }
        if (selectedApartment !== 0) {
            return [selectedApartment];
        }
        return filteredApartmentIds;
    }, [filteredApartmentIds, selectedApartment]);

    const {
        data: occupanciesGroup,
        isLoading: isLoadingGroup,
        error: errorGroup,
    } = useGetOccupanciesQuery({
        date: currentDate.toISOString().split("T")[0],
        apartmentId: 0,
        groupId: selectedGroup,
        apartmentIds: groupCardApartmentIds,
    });

    const { data: occupanciesGroupPrev, isLoading: isLoadingGroupPrev } = useGetOccupanciesQuery({
        date: previousYearsDate.toISOString().split("T")[0],
        apartmentId: 0,
        groupId: selectedGroup,
        apartmentIds: groupCardApartmentIds,
    });

    const {
        data: occupanciesApartment,
        isLoading: isLoadingApartment,
        error: errorApartment,
    } = useGetOccupanciesQuery({
        date: currentDate.toISOString().split("T")[0],
        groupId: 0,
        apartmentId: selectedApartment,
        apartmentIds: apartmentCardApartmentIds,
    });

    const { data: occupanciesApartmentPrev, isLoading: isLoadingApartmentPrev } = useGetOccupanciesQuery({
        date: previousYearsDate.toISOString().split("T")[0],
        groupId: 0,
        apartmentId: selectedApartment,
        apartmentIds: apartmentCardApartmentIds,
    });

    return (
        <>
            <div className="space-y-6" data-testid="occupancies-page">
                <ApartmentFilterBar />
                {/* Filters */}
                <div className="flex justify-end">
                    <div className="mr-6">
                        <DatePicker variant="volt" selectedValue={currentDate} onChange={setDate} />
                    </div>
                </div>

                <Card className="shadow-sm" data-testid="occupancies-group-card">
                    <div className="flex justify-between p-6">
                        <h2 className="font-bold">
                            Occupancy Explorer - Group : {selectedGroup === 0 ? "All" : groupIdMap.get(selectedGroup)?.name}
                        </h2>
                        <div data-testid="occupancies-group-select">
                            <SearchableSelect
                                variant="volt"
                                selectedValue={selectedGroup.toString()}
                                onChange={(value) => {
                                    setSelectedGroup(Number(value));
                                }}
                                placeholder="Select Group"
                                options={filteredGroupOptions}
                                selectedValuePrefix="Group"
                            />
                        </div>
                    </div>
                    {isLoadingGroup || isLoadingGroupPrev ? (
                        <div className="flex justify-center items-center min-h-[200px]">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : errorGroup ? (
                        <Alert variant="destructive">
                            <AlertDescription>{errorGroup.toString()}</AlertDescription>
                        </Alert>
                    ) : occupanciesGroup !== undefined && occupanciesGroup.length === 0 ? (
                        <Alert>
                            <AlertDescription>
                                No data available for the selected date range. Please try a different range.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <CardContent className="p-6">
                            {occupanciesGroup !== undefined &&
                                occupanciesGroup.length > 0 &&
                                occupanciesGroupPrev !== undefined &&
                                occupanciesGroupPrev.length > 0 && (
                                    <YearOverYearChart
                                        stats={[...occupanciesGroup, ...occupanciesGroupPrev].map((s) => ({
                                            date: s.date,
                                            value: s.occupancy,
                                        }))}
                                        valueFormatter={(value: number) => value.toFixed(0) + "%"}
                                        startFromMonth={currentDate.getMonth()}
                                    />
                                )}
                        </CardContent>
                    )}
                </Card>

                <Card className="shadow-sm" data-testid="occupancies-apartment-card">
                    <div className="flex justify-between p-6">
                        <h2 className="font-bold">
                            Occupancy Explorer - Apartment: {selectedApartment === 0 ? "All" : apartmentEntities[selectedApartment]?.name}
                        </h2>
                        <div data-testid="occupancies-apartment-select">
                            <SearchableSelect
                                variant="volt"
                                selectedValue={selectedApartment.toString()}
                                onChange={(value) => setSelectedApartment(Number(value))}
                                placeholder="Select Apartment"
                                options={filteredApartmentOptions}
                                selectedValuePrefix="Apartment"
                                width="w-auto"
                            />
                        </div>
                    </div>
                    {isLoadingApartment || isLoadingApartmentPrev ? (
                        <div className="flex justify-center items-center min-h-[200px]">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    ) : errorApartment ? (
                        <Alert variant="destructive">
                            <AlertDescription>{errorApartment.toString()}</AlertDescription>
                        </Alert>
                    ) : occupanciesApartment !== undefined && occupanciesApartment.length === 0 ? (
                        <Alert>
                            <AlertDescription>
                                No data available for the selected date range. Please try a different range.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <CardContent className="p-6">
                            {occupanciesApartment !== undefined &&
                                occupanciesApartment.length > 0 &&
                                occupanciesApartmentPrev !== undefined &&
                                occupanciesApartmentPrev.length > 0 && (
                                    <YearOverYearChart
                                        stats={[...occupanciesApartment, ...occupanciesApartmentPrev].map((s) => ({
                                            date: s.date,
                                            value: s.occupancy,
                                        }))}
                                        valueFormatter={(value: number) => value.toFixed(0) + "%"}
                                        startFromMonth={currentDate.getMonth()}
                                    />
                                )}
                        </CardContent>
                    )}
                </Card>
            </div>
        </>
    );
};

export default OccupanciesPage;
